/**
 * Copy one recipe/food into one or more (meal type, option) slots on the SAME
 * plan or template, possibly crossing meal types (e.g. Lunch/Option 1 -> Dinner/Option 1).
 * Resolves option ids per-target from the full `dataByPeriod` map, so targets
 * can span any meal type already on the plan/template.
 */
import { formatOptionTitle, findOptionId } from "../utils/formatOptionTitle";
import {
  modifyTargetPlanMealOptionFoods,
  modifyTemplateMealOptionFoods,
} from "../api/dietApi";
import { getMealFoodId } from "./recipeId";
import { formatIntakeTimeForApi } from "./intakeTime";

/**
 * @param {Object} params
 * @param {string} params.eventTypeId
 * @param {"plan"|"template"} params.context
 * @param {Object} params.food - source food row (food_id, quantity, quantity_type_id, intake_time, recipe_details, ...)
 * @param {string} params.sourcePeriodName - meal type the food is being copied FROM
 * @param {Array<{periodName: string, optionTab: string|number}>} params.targets
 * @param {Object} params.dataByPeriod - { [periodName]: { optionIds, foods } } for the whole plan/template
 * @returns {Promise<{ success: boolean, applied: Array, skippedDuplicate: Array, skippedMissing: Array, message?: string }>}
 */
export async function copyFoodToMealTargets({
  eventTypeId,
  context,
  food,
  sourcePeriodName,
  targets,
  dataByPeriod,
}) {
  const applied = [];
  const skippedDuplicate = [];
  const skippedMissing = [];

  const recipeId = getMealFoodId(food);
  if (!eventTypeId || !recipeId || !targets?.length || !dataByPeriod) {
    return { success: false, applied, skippedDuplicate, skippedMissing };
  }

  const quantity = food.quantity || 1;
  const optionIdField =
    context === "plan" ? "target_plan_meal_option_id" : "template_meal_option_id";
  const rows = [];

  targets.forEach((target) => {
    const { periodName, optionTab } = target;
    const periodData = dataByPeriod[periodName];
    const optionId = findOptionId(periodData?.optionIds, optionTab);
    if (!optionId) {
      skippedMissing.push(target);
      return;
    }
    const optionTitle = formatOptionTitle(optionTab);
    const alreadyExists = (periodData.foods || []).some(
      (f) => f.option === optionTitle && String(f.food_id) === String(recipeId)
    );
    if (alreadyExists) {
      skippedDuplicate.push(target);
      return;
    }
    const isCrossMealType = periodName !== sourcePeriodName;
    rows.push({
      row: {
        operation: "ADD",
        [optionIdField]: optionId,
        food_id: recipeId,
        quantity,
        quantity_type_id: food.quantity_type_id,
        intake_time: isCrossMealType
          ? null
          : formatIntakeTimeForApi(food.intake_time || food.time || ""),
      },
      target,
    });
  });

  if (!rows.length) {
    return { success: false, applied, skippedDuplicate, skippedMissing };
  }

  const res =
    context === "plan"
      ? await modifyTargetPlanMealOptionFoods({
          event_type_id: eventTypeId,
          meal_option_foods: rows.map((r) => r.row),
        })
      : await modifyTemplateMealOptionFoods({
          event_type_id: eventTypeId,
          foods: rows.map((r) => r.row),
        });

  if (!res?.success) {
    return {
      success: false,
      applied,
      skippedDuplicate,
      skippedMissing,
      message: res?.message,
    };
  }

  rows.forEach((r) => applied.push(r.target));
  return { success: true, applied, skippedDuplicate, skippedMissing };
}
